import React, {useState} from 'react'
import styled from 'styled-components'

interface CustomTextFieldProps {
  label: string
  value: string
  onChange?: (value: string) => void
  disabled?: boolean
  placeholder?: string
  type?: 'text' | 'number' | 'date' | 'email' | 'password' | 'tel' | 'url'
  error?: string
  helperText?: string
  required?: boolean
  name?: string
  onFocus?: () => void
  onBlur?: () => void
}

const FieldContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${(props: {hasHelperOrError: boolean}) => (props.hasHelperOrError ? '4px' : '0')};
`

const FloatingLabelContainer = styled.div`
  position: relative;
`

const StyledInput = styled.input<{
  hasError: boolean
  isFocused: boolean
  disabled?: boolean
  hasValue: boolean
}>`
  width: 100%;
  height: 44px;
  padding: 20px 12px 8px 12px;
  border-radius: 8px;
  border: ${(props) => {
    if (props.hasError) return '2px solid #E74C3C'
    if (props.isFocused) return '2px solid #6D91F6'
    return '1px solid #c8d6fc'
  }};
  background: ${(props) => (props.disabled ? '#f8f9fa' : '#ffffff')};
  color: ${(props) => (props.disabled ? '#6c757d' : '#212529')};
  font-size: 1rem;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  box-shadow: ${(props) => {
    if (props.hasError)
      return '0px 2px 2px 0px rgba(33, 37, 41, 0.06), 0px 0px 1px 0px rgba(33, 37, 41, 0.08)'
    if (props.isFocused) return '0px 0px 0px 4px rgba(109, 145, 246, 0.28)'
    return 'none'
  }};

  &::placeholder {
    color: transparent;
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const FloatingLabel = styled.label<{
  isFloating: boolean
  hasError: boolean
  isFocused: boolean
  required: boolean
}>`
  position: absolute;
  left: 12px;
  top: 50%;
  color: ${(props) => {
    if (props.hasError) return '#E74C3C'
    if (props.isFocused) return '#6D91F6'
    return '#495057'
  }};
  font-family: 'Inter', sans-serif;
  font-size: ${(props) => (props.isFloating ? '12px' : '16px')};
  font-weight: 400;
  line-height: ${(props) => (props.isFloating ? '18px' : '24px')};
  transition: all 0.2s ease;
  pointer-events: none;
  background: white;
  padding: 0 4px;
  transform: ${(props) =>
    props.isFloating ? 'translateY(-32px) scale(0.85)' : 'translateY(-50%) scale(1)'};
  transform-origin: left center;
  z-index: 1;

  &::after {
    content: ${(props) => (props.required ? '"*"' : '""')};
    color: #e74c3c;
    margin-left: 2px;
  }
`

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #dc3545;
  font-family: 'Inter', sans-serif;
  line-height: 16px;
`

const HelperText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  font-family: 'Inter', sans-serif;
  line-height: 16px;
`

export const CustomTextField: React.FC<CustomTextFieldProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  placeholder = '',
  type = 'text',
  error,
  helperText,
  required = false,
  name,
  onFocus,
  onBlur,
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const hasValue = value.length > 0
  const isFloating = isFocused || hasValue

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value)
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
    onFocus?.()
  }

  const handleBlur = () => {
    setIsFocused(false)
    onBlur?.()
  }

  return (
    <FieldContainer hasHelperOrError={!!(error || helperText)}>
      <FloatingLabelContainer>
        <StyledInput
          type={type}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          hasValue={hasValue}
          hasError={!!error}
          isFocused={isFocused}
          name={name}
        />
        <FloatingLabel
          isFloating={isFloating}
          hasError={!!error}
          isFocused={isFocused}
          required={required}>
          {label}
        </FloatingLabel>
      </FloatingLabelContainer>
      {error && <ErrorText>{error}</ErrorText>}
      {helperText && !error && <HelperText>{helperText}</HelperText>}
    </FieldContainer>
  )
}
